/**
 * AIFC prompt-and-modal presentation eligibility check.
 *
 * Covers the prompt box treatment only — the narrower modal inset and its
 * re-centre offset (`ModalRegion/utils.ts`), the AIFC prompt placeholder, the
 * inline agent selector (`RovoPromptScreenWithLogic.tsx`), and inline-comment
 * prompt placement (`ModalRegion/utils.ts`).
 *
 * Two independent switches turn it on, so the treatment can ship either on its
 * own or together with the rest of the M1 streaming UX:
 *
 * - the dedicated `platform_editor_ai_prompt_updates` experiment, or
 * - the `enablePromptUpdates` parameter of
 *   `platform_editor_ai_streaming_ux_experience_m1`.
 *
 * The dedicated experiment is evaluated first so an enrolled user never needs
 * the M1 read. Both switches default to `false`, so an absent or uninitialised
 * value leaves the legacy prompt presentation in place.
 *
 * Callers must pick the right variant:
 *
 * - `isPromptUpdatesEnabled` logs an experiment exposure. It is called once per
 *   modal open from `openAIModal`, which is the canonical enrolment point.
 * - `isPromptUpdatesEnabledNoExposure` logs nothing. Every presentation site
 *   uses it, because they all run on render paths.
 */
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';

import { STREAMING_UX_EXPERIENCE_M1 } from './streaming-ux-experience-m1-enabled';

/**
 * Switcheroo key for the dedicated prompt-updates experiment.
 *
 * Exported so tests and callers reference a single source of truth for the
 * experiment name.
 */
export const PROMPT_UPDATES_EXPERIMENT = 'platform_editor_ai_prompt_updates';

/**
 * Parameter of the M1 streaming UX experiment that opts the M1 cohort into the
 * prompt updates without a separate rollout.
 */
export const M1_PROMPT_UPDATES_PARAM = 'enablePromptUpdates';

/**
 * Exposure-logging variant. Use it only at action-driven decision points - see
 * file-level JSDoc.
 */
export function isPromptUpdatesEnabled(): boolean {
	return (
		isExperimentEnabled(PROMPT_UPDATES_EXPERIMENT) ||
		expVal(STREAMING_UX_EXPERIENCE_M1, M1_PROMPT_UPDATES_PARAM, false)
	);
}

/**
 * Non-exposure-logging variant for render paths - see file-level JSDoc.
 */
export function isPromptUpdatesEnabledNoExposure(): boolean {
	return (
		UNSAFE_expValNoExposure(PROMPT_UPDATES_EXPERIMENT, 'isEnabled', false) ||
		UNSAFE_expValNoExposure(STREAMING_UX_EXPERIENCE_M1, M1_PROMPT_UPDATES_PARAM, false)
	);
}
